import { graphql } from 'gatsby';
import React from 'react';
import Layout from '../Layout';
import Seo from '../components/global/Seo';
import AdditionalInfo from '../components/workSingle/AdditionalInfo';
import Hero from '../components/workSingle/Hero';
import Lede from '../components/workSingle/Lede';
import MediaWrapper from '../components/workSingle/MediaWrapper';
import NextPrevProject from '../components/workSingle/NextPrevProject';
import ProjectAwards from '../components/workSingle/ProjectAwards';
import ProjectInfo from '../components/workSingle/ProjectInfo';
import Text from '../components/workSingle/Text';
import {
  next,
  nextPrevHeading,
  nextPrevious,
  previous,
} from './work-single.module.scss';

const WorkSingle = ({ data }) => {
  const { hero, client, services, project_link, more_info, awards, slug } =
    data.strapiWork;
  const lede = data.strapiWork.lede.data.lede;

  // Consecutive images or videos should be grouped to have a single background color
  const bodyComponents = combineMedia(data.strapiWork.body.map((c) => c));

  function combineMedia(body) {
    let i = body.findIndex(isMedia);
    if (i === -1) return body;
    let endIndex = body.slice(i).findIndex((comp) => !isMedia(comp));
    const spliceLength = endIndex === -1 ? body.length - i : endIndex;
    const multiImg = body.splice(i, spliceLength).reduce(
      (el, current) => {
        el.media.push(current);
        return el;
      },
      { strapi_component: 'simple.media', media: [] }
    );
    return body.concat(multiImg, combineMedia(body.splice(i, body.length - i)));
  }

  function isMedia({ strapi_component }) {
    return (
      strapi_component === 'simple.image' ||
      strapi_component === 'simple.video-link'
    );
  }

  // Find the Previous and Next Projects
  const thisIndex = data.allStrapiWork.nodes
    .map((work) => {
      return work.slug;
    })
    .indexOf(slug);
  const previousProject = data.allStrapiWork.nodes[thisIndex - 1];
  const nextProject = data.allStrapiWork.nodes[thisIndex + 1];

  return (
    <Layout>
      <section className={`bgSizeTrigger bgColorTrigger`} data-color="Milk">
        {hero && hero.hero_image && <Hero heroMedia={hero} />}
        {lede && <Lede richText={lede} />}
        {(client || services) && (
          <ProjectInfo
            title_creative={data.strapiWork.title_creative}
            title_descriptive={data.strapiWork.title_descriptive}
            link={project_link}
            client={client}
            services={services}
          />
        )}
      </section>
      <section className={`bgColorTrigger`} data-color="Milk">
        {bodyComponents.map(
          ({ strapi_component, id, media, text_content }, index) => {
            return {
              // TODO try wrapping the background component here somehow, it might make the resizing easier
              'simple.media': <MediaWrapper media={media} key={index} />,
              'simple.text': (
                <Text textContent={text_content} id={id} key={index} />
              ),
            }[strapi_component];
          }
        )}
      </section>
      {more_info && more_info.length > 0 && (
        <section
          className={`bgColorTrigger pagePadding pageGrid sectionVertPadding`}
          data-color="Milk"
        >
          {more_info.map(({ heading, text, quote_attr, link }, index) => (
            <AdditionalInfo
              heading={heading}
              text={text}
              quoteAttr={quote_attr}
              link={link}
              key={index}
            />
          ))}
        </section>
      )}
      {awards.length > 0 && (
        <section
          className={`bgColorTrigger pagePadding pageGrid sectionVertPadding`}
          data-color="Ice"
        >
          <ProjectAwards awards={awards} />
        </section>
      )}
      <section
        className={`bgColorTrigger pagePadding pageGrid sectionVertPadding`}
        data-color="Ice"
      >
        <div className={`scrollFadeIn gridAllColumns ${nextPrevious}`}>
          {nextProject && (
            <div className={`${next}`}>
              <h3 className={`h5 ${nextPrevHeading}`}>Next</h3>
              <NextPrevProject
                title_creative={nextProject.title_creative}
                title_descriptive={nextProject.title_descriptive}
                client={nextProject.client?.name}
                projectSlug={`/work/${nextProject.slug}`}
                hero={nextProject.hero}
              />
            </div>
          )}
          {previousProject && (
            <div className={`${previous}`}>
              <h3 className={`h5 ${nextPrevHeading}`}>Previous</h3>
              <NextPrevProject
                title_creative={previousProject.title_creative}
                title_descriptive={previousProject.title_descriptive}
                client={previousProject.client?.name}
                projectSlug={`/work/${previousProject.slug}`}
                hero={previousProject.hero}
                previous
              />
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export const Head = ({ data }) => {
  const { title_creative, title_descriptive, lede, client, hero } =
    data.strapiWork;
  const title = title_creative ? title_creative : title_descriptive;
  const desc = `${lede.data.lede} Learn more about our ${title_descriptive}${
    client ? ` for ${client.name}.` : '.'
  }`;
  return <Seo pageTitle={title} description={desc} image={hero?.hero_image} />;
};

export const query = graphql`
  query ($slug: String!) {
    strapiWork(slug: { eq: $slug }) {
      slug
      title_creative
      title_descriptive
      project_link
      client {
        name
        link
      }
      lede {
        data {
          lede
        }
      }
      hero {
        background_color {
          custom_color
          preset_color
        }
        frame
        video_link
        video_link_local
        hero_image {
          alternativeText
          width
          height
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData(
                quality: 80
                layout: FULL_WIDTH
                placeholder: NONE
                aspectRatio: 1.778
              )
            }
          }
        }
      }
      services {
        tile_title
        tile_id
      }
      body {
        ... on STRAPI__COMPONENT_SIMPLE_IMAGE {
          strapi_component
          id
          frame
          caption {
            data {
              caption
            }
          }
          background_color {
            preset_color
            custom_color
          }
          content {
            alternativeText
            caption
            width
            height
            localFile {
              childImageSharp {
                gatsbyImageData(
                  quality: 80
                  layout: FULL_WIDTH
                  placeholder: BLURRED
                )
              }
            }
          }
        }
        ... on STRAPI__COMPONENT_SIMPLE_TEXT {
          strapi_component
          id
          text_content {
            data {
              text_content
            }
          }
        }
        ... on STRAPI__COMPONENT_SIMPLE_VIDEO_LINK {
          strapi_component
          id
          autoplay
          link_content
          local_filename 
          is_vr
          frame
          video_description
          background_color {
            custom_color
            preset_color
          }
          video_cover {
            width
            height
            localFile {
              childImageSharp {
                gatsbyImageData(
                  quality: 80
                  layout: FULL_WIDTH
                  placeholder: BLURRED
                )
              }
            }
          }
        }
      }
      more_info {
        heading
        text {
          data {
            text
          }
        }
        quote_attr
        link {
          data {
            link
          }
        }
      }
      awards {
        category
        name
        year
        color
      }
    }
    allStrapiWork(sort: { tile_order: DESC }) {
      nodes {
        client {
          name
        }
        title_creative
        title_descriptive
        slug
        hero {
          background_color {
            custom_color
            preset_color
          }
          frame
          video_link
          video_link_local
          hero_image {
            width
            height
            alternativeText
            localFile {
              childImageSharp {
                gatsbyImageData(
                  quality: 80
                  layout: FULL_WIDTH
                  placeholder: BLURRED
                  aspectRatio: 1.778
                )
              }
            }
          }
          square_image {
            width
            height
            alternativeText
            localFile {
              childImageSharp {
                gatsbyImageData(
                  quality: 80
                  layout: FULL_WIDTH
                  placeholder: BLURRED
                  aspectRatio: 1
                  sizes: "25vw"
                  breakpoints: [400, 600]
                )
              }
            }
          }
        }
      }
    }
  }
`;

export default WorkSingle;
